<template>
    <div class="pa-4 fill-height d-flex flex-column flex-grow-1">
        <AgGrid class="flex-grow-1" ref="ressourceGridRef" :fetchUrl="ressourceFetchUrl"
            :columnDefs="ressourceColumnDefs" :gridParams="ressourceGridParams" :refresh="ressourceRefresh" :gridOptions="ressourceGridOptions"
            :domLayout="domLayout" />
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue';
import AgGrid from '@/components/AgGrid.vue';
import countriesJson from '@/components/json/countries.json';
import { toast } from 'vue3-toastify';
import { useControlStore } from '@/stores/controlStore';
import { useLogStore } from '@/stores/logStore';
import { useUserStore } from '@/stores/userStore';

const props = defineProps({
    customerReference: {
        type: String,
        default: null,
    },
});
const emit = defineEmits(['pending-changes']);

const urlWebAPI = inject('urlWebAPI');
const translate = inject('translate');
const getFormBody = inject('getFormBody');

const controlStore = useControlStore();
const logStore = useLogStore();
const userStore = useUserStore();

const domLayout = 'normal';
const countries = countriesJson;

const ressourceFetchUrl = 'GetApplicationRessources';
const ressourceColumnDefs = ref([
    { field: 'application', rowGroup: true, hide: true },
    {
        field: 'name',
        headerName: translate('name', 'column'),
        sort: 'asc',
        pinned: 'left',
        lockPinned: true
    },
    {
        field: 'locales',
        headerName: translate('locales', 'column'),
        children: getLocaleColumns(),
    },
]);
const ressourceGridParams = {
    token: userStore.getUser().token,
    customerReference: props.customerReference,
};
const ressourceGridOptions = {
    getRowId:(params) => params.data.application + '_' + params.data.id,
};
const ressourceRefresh = ref({
    state: false,
    type: 'column',
});

const rowsUpdated = ref([]);

const hasPendingChanges = computed(() => rowsUpdated.value.length > 0);

watch(hasPendingChanges, (value) => {
    emit('pending-changes', value);
});

onMounted(() => {
    getLocaleNumber();
});

function getLocaleColumns() {
    var localesColumns = [];
    var countries = countriesJson;

    countries.forEach((country) => {
        localesColumns.push({
            cellRenderer: 'translationValueCellRenderer',
            cellEditor: 'agTextCellEditor',
            field: country.code,
            headerName: `${country.language} (${country.code})`,
            headerComponentParams: {
                innerHeaderComponent: 'localeInnerHeaderComponent',
                innerHeaderComponentParams: { countryCode: country.code },
            },
            editable: true,
            enableCellChangeFlash: true,
            type: 'language',
        });

        localesColumns.push({
            minWidth: 55,
            maxWidth: 55,
            suppressFiltersToolPanel: true,
            suppressHeaderFilterButton: true,
            suppressHeaderMenuButton: true,
            sortable: false,
            filter: false,
            cellRenderer: 'translationValueCellRenderer',
            filterParams: {
                cellRenderer: 'translationValueCellRenderer',
            },
            cellEditor: 'agTextCellEditor',
            field: `isCustom${country.code}`,
            headerName: `${translate('isCustom', 'column')} (${country.code})`,
            headerComponentParams: {
                innerHeaderComponent: 'localeInnerHeaderComponent',
                innerHeaderComponentParams: { countryCode: country.code },
            },
            editable: false,
            type: 'language',
            valueGetter: (params) => {
                return Object.keys(params.data.localesCustom).indexOf(params.colDef.field.replace('isCustom', '').toLowerCase()) > -1
                    && params.data.localesCustom[params.colDef.field.replace('isCustom', '').toLowerCase()];
            },
        });

        localesColumns.push({
            field: `localeId${country.code}`,
            headerName: `localeId ${country.code}`,
            editable: false,
            hide:true, //to deactivate if need to debug
            enableCellChangeFlash: true,
            valueGetter: (params) => {
                return Object.keys(params.data.localeIds).indexOf(params.colDef.field.replace('localeId', '').toLowerCase()) > -1
                    && params.data.localeIds[params.colDef.field.replace('localeId', '').toLowerCase()];
            },
        })
    });

    return localesColumns;
}

function getLocaleNumber() {
    var formBody = getFormBody(ressourceGridParams);

    fetch(urlWebAPI + 'GetLocaleNumber', {
        method: 'POST',
        body: formBody,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        }
    })
        .then(result => result.json())
        .then(locales => {
            var updatedColumnDefs = ressourceColumnDefs.value.filter(rc => rc.field != 'locales');

            var localeColumnDef = ressourceColumnDefs.value.filter(rc => rc.field == 'locales').first();
            localeColumnDef.children = localeColumnDef.children.filter(c =>
                locales.indexOf(c.field.toLowerCase()) > -1 ||
                locales.map(l => 'iscustom' + l).indexOf(c.field.toLowerCase()) > -1 ||
                locales.map(l => 'localeid' + l).indexOf(c.field.toLowerCase()) > -1);
            updatedColumnDefs.push(localeColumnDef);

            ressourceColumnDefs.value = updatedColumnDefs;

            ressourceRefresh.value = { type: 'column', state: true };

            setTimeout(() => {
                ressourceRefresh.value = { ...ressourceRefresh.value, state: false };
            }, 1000);
        })
        .catch(error => {
            logStore.log(error, 'error');
            toast.error('Error during executing GetLocaleNumber data.');
        });
}

function save() {
    let data = {
        token: userStore.getUser().token,
        customerReference: props.customerReference,
        ressources: rowsUpdated.value,
    };

    $.postData(urlWebAPI + 'SaveRessources', data)
        .then(result => {
            mergeResponse(result, rowsUpdated.value);
            rowsUpdated.value = [];
            toast.success(translate('customerParamsWellSaved', 'toast'), {theme: 'colored'});
        })
        .catch(error => {
            logStore.log(error, 'error');
            toast.error('Error during executing SaveRessources data.');
        });
}

function mergeResponse(apiResult, rowsUpdatedList) {
    //1st step merge apiResult with rowsUpdated that has been sent
    const result = rowsUpdatedList.map(rowUpdatedItem => {

        const matchingApi = apiResult.filter(
            apiItem =>
                apiItem.application === rowUpdatedItem.application &&
                apiItem.id === rowUpdatedItem.id
        );

        const locales = {};
        const localeIds = {};
        const localesCustom = {};

        // FR est toujours conservé depuis rowsUpdated
        if (rowUpdatedItem.locales?.fr !== undefined) {
            locales.fr = rowUpdatedItem.locales.fr;
            localesCustom.fr = rowUpdatedItem.localesCustom?.fr ?? false;

            if (rowUpdatedItem.localeIds?.fr !== undefined) {
                localeIds.fr = rowUpdatedItem.localeIds.fr;
            }
        }

        // Traitement des locales présentes dans apiResult
        matchingApi.forEach(apiItem => {
            // FR est déjà traité
            if (apiItem.locale === "fr") {
                return;
            }

            const oldValue = rowUpdatedItem.locales?.[apiItem.locale];

            locales[apiItem.locale] = apiItem.value;
            localeIds[apiItem.locale] = apiItem.valueId;

            if (oldValue !== undefined) {
            // On ne modifie localesCustom que si la valeur a réellement changé
                if (oldValue !== apiItem.value) {
                    localesCustom[apiItem.locale] = true;
                } else if (rowUpdatedItem.localesCustom?.[apiItem.locale] !== undefined) {
                        // Valeur identique : on conserve le statut actuel
                        localesCustom[apiItem.locale] = rowUpdatedItem.localesCustom[apiItem.locale];
                    }
            }
            else {
                localesCustom[apiItem.locale] = true;
            }
        });

        return {
            ...rowUpdatedItem,
            locales,
            localeIds,
            localesCustom
        };
        });

    result.forEach(rowUpdated => {
        //the key (rowUpdated.application+'_'+rowUpdated.id) used in getRowNode is defined in ressourceGridOptions => getRowId
        var rowNode = controlStore.getDisplayedGrid().api.getRowNode(rowUpdated.application+'_'+rowUpdated.id);
        rowNode.updateData(rowUpdated);
    });
}

function undo() {
    var gridApi = controlStore.getDisplayedGrid().api;

    for (var i = 0; i < gridApi.getCurrentUndoSize() + 1; i++)
        setTimeout(() => {
            gridApi.undoCellEditing();
        }, 10 * i);

    gridApi.redrawRows();
    rowsUpdated.value = [];
}

// ViewManageCustomer.vue calls undo()/save() through a template ref, and AgGrid.vue reaches
// into rowsUpdated through the $parent chain — both only see what defineExpose() exposes.
defineExpose({ undo, save, rowsUpdated });
</script>
